import { Card, ModalDialog, ModalMessage, ModalMessageColorsEnum } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { VideoModal } from '@/components/VideoModal/VideoModal';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { findSegmentByDate } from '@/features/broadcast-schedule-program/helpers/find-segment-by-date';
import { TToolkitComponentProps } from '@/features/broadcast-schedule-program/types';
import { useDeleteClip } from '@/features/clips/api/delete-clips';
import { useClipPermissions } from '@/features/clips/hooks/useClipPermissions';
import { TClip } from '@/features/clips/types';
import { ReviewStateEnum } from '@/features/segments/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { convertDatesToSeconds } from '@/utils/hh-mm-ss-into-number';

import { useNavigationToolkitItem } from '../../hooks/useNavigationToolkitItem';
import { ESegmentToolkit } from '../../types';
import { BroadcastScheduleProgramBodySegmentToolkitNavigation } from '../BroadcastScheduleProgramBodySegmentToolkitNavigation';

import { BroadcastScheduleProgramBodySegmentToolkitCutForm } from './components/BroadcastScheduleProgramBodySegmentToolkitCutForm';
import { BroadcastScheduleProgramBodySegmentToolkitCutTable } from './components/BroadcastScheduleProgramBodySegmentToolkitCutTable';

export default function BroadcastScheduleProgramBodySegmentToolkitCut({
  programBroadcast,
}: TToolkitComponentProps) {
  const {
    formBox,
    cutData,
    clipBySegmentItems,
    totalClipsBySegment,
    isLoadingClipsBySegment,
    segmentItems,
    activeSegment,
    setFormBox,
    setCutData,
    setActiveSegment,
    setPendingSeekTime,
    videoControlRef,
    handleTooltikClick,
  } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const { gte, lte } = useProgramBroadcastFilters();
  const dialog = useDisclosureWithData<TClip>();
  const { mutateAsync: deleteClip } = useDeleteClip();
  const recordingFilePreviewDialog = useDisclosureWithData<{ url: string; body: string }>();

  const { canCreate, canUpdate, canDelete } = useClipPermissions();

  const isClipInIncidenceSegment = useCallback(
    (clip: TClip) => {
      const segment = findSegmentByDate(segmentItems, clip.start_datetime);
      return segment?.review_state === ReviewStateEnum.INCIDENCE;
    },
    [segmentItems]
  );

  const { handleNextItemWithoutIncidence } = useNavigationToolkitItem({
    items: clipBySegmentItems,
    activeItem: cutData,
    onChangeData: setCutData,
    isItemDisabled: isClipInIncidenceSegment,
  });

  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const skipEffectRef = useRef(false);

  /**
   * Whether a cut can be made in the active block.
   *
   * Under a review the emission answers it: this panel is opened from an emission's own
   * segment strip, so every block in it is inside that emission by construction. Asking
   * the block instead asked the one field in the chain that is written asynchronously —
   * a block absorbed by moving an edge is returned before the association is written
   * (RCOMM-6562), so `programs` was empty and the button vanished on the block the
   * reviewer had just added in order to cut in it.
   *
   * Nothing downstream wanted that field anyway: the form takes its programme from the
   * emission, and the backend resolves the cut's own emission from that programme and
   * the cut's window — against a window this same request has already moved.
   *
   * With no emission in context — the global segments view — a block with no programme
   * genuinely has none, and the notice stays true. With no block at all there is nothing
   * to cut in, emission or not, as before.
   */
  const canCutInActiveSegment = !activeSegment
    ? false
    : programBroadcast
      ? !!programBroadcast.program_id
      : (activeSegment.programs?.length ?? 0) > 0;

  const handleEdit = useCallback(
    (item: TClip) => {
      if (!canUpdate) return;
      setFormBox('cut');
      setCutData(item);
    },
    [setFormBox, canUpdate]
  );

  const handleCreate = useCallback(() => {
    if (!canCreate || !canCutInActiveSegment) return;
    setFormBox('cut');
    setCutData(null);
  }, [setFormBox, setCutData, canCreate, canCutInActiveSegment]);

  const handleOpenDialog = useCallback(
    (item: TClip) => {
      if (!canDelete) return;
      dialog.open(item);
    },
    [canDelete]
  );

  const handleDelete = useCallback(async () => {
    if (!dialog.data) return;
    await deleteClip({ id: dialog.data?.id || '' }).then(() => {
      dialog.close();
    });
  }, [deleteClip, dialog]);

  const handleRowClick = useCallback(
    (item: TClip) => {
      if (!segmentItems) return;

      const segment = findSegmentByDate(segmentItems, item.start_datetime);
      if (!segment) return;

      const seconds = convertDatesToSeconds(item.start_datetime, segment.start_datetime);

      skipEffectRef.current = true;
      setSelectedClipId(item.id);

      if (activeSegment?.id !== segment.id) {
        setActiveSegment(segment);
        setPendingSeekTime(seconds);
      } else {
        videoControlRef.current?.seekToSecond(seconds);
      }
    },
    [segmentItems, activeSegment, setActiveSegment, setPendingSeekTime, videoControlRef]
  );

  useEffect(() => {
    if (skipEffectRef.current) {
      skipEffectRef.current = false;
      return;
    }
    if (!activeSegment) {
      setSelectedClipId(null);
      return;
    }
    const segStart = new Date(activeSegment.start_datetime).getTime();
    const segEnd = segStart + activeSegment.duration_in_seconds * 1000;
    const firstClip = clipBySegmentItems.find(clip => {
      const clipStart = new Date(clip.start_datetime).getTime();
      return clipStart >= segStart && clipStart <= segEnd;
    });
    setSelectedClipId(firstClip?.id ?? null);
  }, [activeSegment]);

  const handleRedirect = useCallback(
    (item: TClip) => {
      if (!programBroadcast) return;
      const params = new URLSearchParams({
        id: item.id,
        gte: item.start_datetime,
        lte: item.end_datetime,
        program_id: programBroadcast.id,
        program_gte: gte || programBroadcast.start_datetime,
        program_lte: lte || programBroadcast.end_datetime,
      });
      window.open(`/app/review/clips?${params.toString()}`, '_blank');
    },
    [gte, lte, programBroadcast]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        handleCreate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreate]);

  return (
    <>
      <Card
        classes={{
          rootSection: clsx('w-full flex-auto order-3 lg:order-2'),
          container: 'py-size-4',
        }}
      >
        <div className="flex w-full flex-col gap-2">
          {formBox !== 'cut' ? (
            <>
              <GeneralHeader
                name={t('Cortes del programa')}
                buttonName={canCreate && canCutInActiveSegment ? t('Añadir') : undefined}
                onClick={canCreate && canCutInActiveSegment ? handleCreate : undefined}
                onClose={() => handleTooltikClick(ESegmentToolkit.CORTES)}
                sectionNotice={
                  !canCutInActiveSegment ? t('Franja sin programa asociado') : undefined
                }
              />
              <BroadcastScheduleProgramBodySegmentToolkitCutTable
                items={clipBySegmentItems}
                totalItems={totalClipsBySegment}
                isLoadingClipsBySegment={isLoadingClipsBySegment}
                selectedClipId={selectedClipId}
                onPreview={recordingFilePreviewDialog.open}
                onUpdate={canUpdate ? handleEdit : undefined}
                onDelete={canDelete ? handleOpenDialog : undefined}
                onRedirect={handleRedirect}
                onRowClick={handleRowClick}
              />
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <BroadcastScheduleProgramBodySegmentToolkitCutForm
                programBroadcast={programBroadcast}
                onAfterSave={handleNextItemWithoutIncidence}
              />

              <BroadcastScheduleProgramBodySegmentToolkitNavigation
                items={clipBySegmentItems}
                activeItem={cutData}
                onChangeData={setCutData}
              />
            </div>
          )}
        </div>
      </Card>
      {createPortal(
        <ModalDialog
          isOpen={dialog.isOpen}
          onClose={dialog.close}
          onAction={handleDelete}
          heading={t('Eliminar corte')}
          actionLabel={t('Eliminar')}
          linkLabel={t('Cancelar')}
          color={ModalMessageColorsEnum.ERROR}
        >
          <ModalMessage title={t('¿Estás seguro de querer eliminar el corte seleccionado?')} />
        </ModalDialog>,
        document.body
      )}

      <VideoModal
        isOpen={recordingFilePreviewDialog.isOpen}
        onClose={recordingFilePreviewDialog.close}
        url={recordingFilePreviewDialog.data?.url || ''}
        title={recordingFilePreviewDialog.data?.body || ''}
      />
    </>
  );
}
